import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Zap, Video, Image as ImageIcon, Wand2, Layers } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import R3FStage from '../cinematic/R3FStage';
import MagneticButton from '../cinematic/MagneticButton';
import SplitText from '../cinematic/SplitText';
import NumberTicker from '../cinematic/NumberTicker';
import BrandMarquee from '../cinematic/BrandMarquee';
import { useAuth } from '../context/AuthContext';

export default function CinematicLanding() {
  const { user } = useAuth();
  const progressRef = useRef(0);
  const mouseXRef = useRef(0);
  const mouseYRef = useRef(0);
  const mouseRef = useRef({ x: mouseXRef, y: mouseYRef });
  const [scrollProgress, setScrollProgress] = useState(0);

  // The hero section is 3 viewports tall — scroll within it scrubs the WebGL timeline
  const heroRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let raf;
    const tick = () => {
      const el = heroRef.current;
      if (!el) { raf = requestAnimationFrame(tick); return; }
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const passed = Math.min(total, Math.max(0, -rect.top));
      const p = total > 0 ? passed / total : 0;
      progressRef.current = p;
      setScrollProgress(p);
      setScrolled(window.scrollY > 30);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const onMouse = (e) => {
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = -((e.clientY / window.innerHeight) * 2 - 1);
      mouseXRef.current = nx;
      mouseYRef.current = ny;
    };
    window.addEventListener('mousemove', onMouse);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  return (
    <div className="text-white cinematic-grain">
      {/* Persistent WebGL canvas behind everything */}
      <R3FStage progressRef={progressRef} mouseRef={mouseRef} />

      {/* Top nav */}
      <header
        data-testid="cinematic-nav"
        className={`fixed top-0 left-0 right-0 z-40 px-6 md:px-10 py-5 flex items-center justify-between transition-all ${scrolled ? 'backdrop-blur-xl bg-[#0A0A0F]/55 border-b border-white/[0.04]' : ''}`}
      >
        <Link to="/" className="font-black tracking-[0.18em] text-sm" data-testid="logo">
          <span className="text-white">DIS</span><span className="text-[#FF6B4A]">2</span><span className="text-white">ORT</span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-[11px] uppercase tracking-[0.22em] text-white/60">
          <Link to="/models" data-testid="nav-models" className="hover:text-white transition">Models</Link>
          <Link to="/pricing" data-testid="nav-pricing" className="hover:text-white transition">Pricing</Link>
          <Link to="/community" data-testid="nav-community" className="hover:text-white transition">Community</Link>
          <Link to="/about" data-testid="nav-about" className="hover:text-white transition">About</Link>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <MagneticButton strength={0.3} radius={70}>
              <Link to="/generate" data-testid="nav-generate-cta">
                <Button className="bg-[#FF6B4A] hover:bg-[#FF8866] text-white rounded-full px-5 h-9 text-xs font-bold uppercase tracking-[0.18em]">
                  Generate
                </Button>
              </Link>
            </MagneticButton>
          ) : (
            <MagneticButton strength={0.3} radius={70}>
              <Link to="/login" data-testid="nav-signin">
                <Button className="bg-white hover:bg-white/90 text-black rounded-full px-5 h-9 text-xs font-bold uppercase tracking-[0.18em]">
                  Sign in
                </Button>
              </Link>
            </MagneticButton>
          )}
        </div>
      </header>

      {/* HERO — 3-viewport tall scroll cage that drives the WebGL timeline */}
      <section
        ref={heroRef}
        className="relative h-[300vh] z-10"
        data-testid="hero-shatter-section"
      >
        {/* Sticky overlay HTML pinned at top of hero while user scrolls */}
        <div className="sticky top-0 h-screen w-full flex items-center justify-center pointer-events-none overflow-hidden">
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center px-6 pointer-events-auto">
            {/* Overline */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="text-[10px] tracking-[0.4em] uppercase text-[#A3FF00] mb-8 font-mono"
              data-testid="hero-overline"
            >
              · 150 AI Models · One Render Stack ·
            </motion.div>
            {/* HUGE DISTORTED DIS2ORT WORDMARK */}
            <h1
              data-testid="hero-wordmark"
              className="hero-wordmark font-black select-none leading-[0.85] tracking-[-0.05em] text-[clamp(4rem,18vw,18rem)]"
              style={{
                transform: `scale(${1 - scrollProgress * 0.5}) translateZ(0)`,
                opacity: Math.max(0, 1 - scrollProgress * 2.5),
                filter: `blur(${scrollProgress * 16}px) hue-rotate(${scrollProgress * 40}deg)`,
                transition: 'transform 0.05s linear',
              }}
            >
              <span className="hero-letter" style={{ '--i': 0 }}>D</span>
              <span className="hero-letter" style={{ '--i': 1 }}>I</span>
              <span className="hero-letter" style={{ '--i': 2 }}>S</span>
              <span className="hero-letter hero-letter--accent" style={{ '--i': 3 }}>2</span>
              <span className="hero-letter" style={{ '--i': 4 }}>O</span>
              <span className="hero-letter" style={{ '--i': 5 }}>R</span>
              <span className="hero-letter" style={{ '--i': 6 }}>T</span>
            </h1>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.4 }}
              className="mt-12 flex flex-col items-center gap-2"
              style={{ opacity: Math.max(0, 1 - scrollProgress * 3) }}
            >
              <span className="text-[10px] tracking-[0.4em] uppercase text-white/35">Scroll</span>
              <div className="h-12 w-px bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE — brand logos scrolling between hero and content */}
      <section className="relative z-10 py-6 md:py-10 bg-[#0A0A0F]/95 backdrop-blur-xl border-y border-white/[0.05]" data-testid="brand-marquee">
        <BrandMarquee
          items={[
            'DALL·E 3', 'Midjourney v7', 'FLUX Pro', 'Sora 2', 'Kling 2', 'Runway Gen 3',
            'Hunyuan', 'Veo 2', 'Imagen 3', 'Recraft', 'Ideogram', 'Leonardo',
            'Playground', 'SDXL', 'SD 3.5', 'Mochi', 'LTX', 'CogVideoX',
          ]}
          speed="normal"
        />
      </section>

      {/* STATS — count up on scroll into view */}
      <section className="relative z-10 px-6 md:px-12 py-20 bg-[#0A0A0F]" data-testid="stats-strip">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 text-center">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2 font-mono">Total Models</div>
            <div className="font-black text-5xl md:text-6xl bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] bg-clip-text text-transparent tracking-tight">
              <NumberTicker value={150} duration={1.8} />
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2 font-mono">Free Credits</div>
            <div className="font-black text-5xl md:text-6xl bg-gradient-to-r from-[#A3FF00] to-[#FFD93D] bg-clip-text text-transparent tracking-tight">
              <NumberTicker value={50} duration={1.4} />
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2 font-mono">Avg Gen Time</div>
            <div className="font-black text-5xl md:text-6xl bg-gradient-to-r from-[#4A7CFF] to-[#06B6D4] bg-clip-text text-transparent tracking-tight">
              <NumberTicker value={4} duration={1.2} suffix="s" />
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2 font-mono">Cheaper Than</div>
            <div className="font-black text-5xl md:text-6xl bg-gradient-to-r from-[#FFD93D] to-[#FF6B4A] bg-clip-text text-transparent tracking-tight">
              <NumberTicker value={3} duration={1.0} suffix="×" />
            </div>
          </div>
        </div>
      </section>

      {/* SHATTERED — content sections after the galaxy forms */}
      <section className="relative z-10 px-6 md:px-12 py-32 md:py-48 bg-gradient-to-b from-transparent via-[#0A0A0F]/85 to-[#0A0A0F]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.9 }}
          >
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3FF00] mb-5 font-mono">
              · The fragments became the future ·
            </div>
            <h2 className="font-black text-5xl md:text-7xl lg:text-8xl tracking-[-0.04em] leading-[0.95] mb-8">
              <SplitText text="Every model you've heard of." as="span" className="block" stagger={0.025} duration={0.7} />
              <span className="text-[#FF6B4A] italic">
                <SplitText text="One render lane." stagger={0.04} duration={0.8} delay={0.4} />
              </span>
            </h2>
            <p className="text-white/55 text-base md:text-lg max-w-2xl leading-relaxed">
              DALL·E 3, Midjourney v7, FLUX Pro/Schnell, Sora 2, Kling, Runway, Hunyuan, Veo 2, Mochi, LTX, SDXL, SD 3.5, Recraft, Ideogram, Imagen 3, Leonardo, Playground, AuraFlow — plus 130 more. Pay-as-you-go. Credits never expire. 3× cheaper than Higgsfield.
            </p>
          </motion.div>

          {/* Feature grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-20">
            {FEATURES.map((f, i) => (
              <FeatureCard key={f.name} feature={f} index={i} />
            ))}
          </div>

          {/* CTA strip */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-32 p-10 md:p-16 rounded-3xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-xl text-center"
          >
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3FF00] mb-4 font-mono">
              · Ready to generate ·
            </div>
            <h3 className="font-black text-4xl md:text-6xl tracking-[-0.03em] mb-6">
              <SplitText text="Pick a model. Type a prompt." className="block" stagger={0.03} duration={0.6} />
              <span className="text-[#FF6B4A] italic">
                <SplitText text="Get art in 4 seconds." stagger={0.04} duration={0.7} delay={0.4} />
              </span>
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
              <MagneticButton strength={0.3} radius={120}>
                <Link to={user ? "/generate" : "/login"} data-testid="cta-generate">
                  <Button className="bg-[#FF6B4A] hover:bg-[#FF8866] text-white rounded-full px-8 h-14 text-sm font-bold uppercase tracking-[0.18em]">
                    Start generating <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.3} radius={120}>
                <Link to="/models" data-testid="cta-browse">
                  <Button variant="outline" className="border-white/[0.12] hover:border-white/[0.3] text-white rounded-full px-8 h-14 text-sm font-bold uppercase tracking-[0.18em] bg-transparent">
                    Browse 150 models
                  </Button>
                </Link>
              </MagneticButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 md:px-12 py-10 border-t border-white/[0.05] bg-[#0A0A0F]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} DIS2ORT · Pay-as-you-go AI rendering</div>
          <div className="flex items-center gap-6">
            <Link to="/about" data-testid="footer-about" className="hover:text-white">About</Link>
            <Link to="/pricing" data-testid="footer-pricing" className="hover:text-white">Pricing</Link>
            <Link to="/faq" data-testid="footer-faq" className="hover:text-white">FAQ</Link>
            <Link to="/blog" data-testid="footer-blog" className="hover:text-white">Blog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const FEATURES = [
  { icon: Sparkles, name: 'IMAGE GENERATION', tag: 'Text → image · 4s', accent: '#FF6B4A',
    desc: 'Type anything. Pick a style. Get production-grade artwork in seconds. 26 hand-tuned presets cover every aesthetic.' },
  { icon: Video, name: 'AI VIDEO', tag: 'Text → motion', accent: '#E040A0',
    desc: 'Sora 2, Kling, Runway, Veo 2, Hunyuan, Mochi, LTX, CogVideoX — spin your prompt into cinematic motion.' },
  { icon: ImageIcon, name: 'IMAGE → VIDEO', tag: 'Animate stills', accent: '#A3FF00',
    desc: 'Drop any image. Get a cinematic clip with camera moves, motion, atmosphere.' },
  { icon: Wand2, name: 'PROMPT ENHANCE', tag: 'AI rewrites your idea', accent: '#FFD93D',
    desc: 'Stuck with a basic prompt? One click and an LLM rebuilds it with composition, lighting, mood, lens, color.' },
  { icon: Layers, name: '150 MODELS', tag: 'One studio · One tab', accent: '#4A7CFF',
    desc: 'Switch engines without switching tabs. Every major image and video generator in one place.' },
  { icon: Zap, name: 'CREDITS NEVER EXPIRE', tag: 'No subscription', accent: '#06B6D4',
    desc: 'Pay once. Generate forever. Use credits across every model. Cancel-proof, time-bomb-proof.' },
];

function FeatureCard({ feature, index }) {
  const { icon: Icon, name, tag, desc, accent } = feature;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.07 }}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
      data-testid={`feature-${name.toLowerCase().replace(/\s+/g, '-')}`}
      className="group relative p-8 rounded-2xl border border-white/[0.06] bg-white/[0.025] hover:bg-white/[0.045] hover:border-white/[0.14] backdrop-blur-xl transition-colors overflow-hidden"
    >
      <div
        className="absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity"
        style={{ background: accent }}
      />
      <div className="relative">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: `${accent}15`, border: `1px solid ${accent}30` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-2 font-mono">{tag}</div>
        <h3 className="text-2xl font-black tracking-tight mb-3">{name}</h3>
        <p className="text-sm text-white/55 leading-relaxed">{desc}</p>
      </div>
    </motion.div>
  );
}
